import { BaseAPI } from '../BaseApi';
import { eAPITags } from '../eAPITags';
import { UUID } from '../../types/shared/UUID';
import {
  convertFromIInternalContactsDataToIInternalContactsRequest,
  IInternalContactData,
} from '../../types/contacts/IInternalContact';
import {
  convertFromICreateInternalContactDataToICreateInternalContactRequest,
  ICreateInternalContactData,
} from '../../types/contacts/CreateInternalContact';
import {
  convertFromIThirdPartyContactDataToIThirdPartyContactRequest,
  convertFromIThirdPartyContactRequestArrayToIThirdPartyContactDataArray,
  convertFromIThirdPartyContactRequestToIThirdPartyContactData,
  IThirdPartyContactData,
  IThirdPartyContactRequest,
} from '../../types/contacts/IThirdPartyContact';
import {
  convertFromIClientContactDataToICreateClientOrderContactRequest,
} from '../../types/contacts/CreateClientContact';
import {
  convertFromLenderContactDataToLenderContactResponse,
  convertFromLenderContactResponseToLenderContactData,
  convertFromLenderOrderContactResponseArrayToLenderContactDataArray,
  ILenderContactData,
  ILenderContactResponse,
} from '../../types/contacts/ILenderContact';
import {
  convertFromILenderContactDataToICreateLenderOrderContactRequest,
} from '../../types/contacts/CreateLenderContact';
import {
  convertFromClientContactDataToClientContactResponse,
  convertFromClientContactResponseArrayToClientContactDataArray,
  convertFromClientContactResponseToClientContactData,
  IClientContactData,
  IClientContactResponse,
} from '../../types/contacts/IClientContact';
import { IConversationContactData } from '../../types/conversations/IConversationContact';

interface AddInternalContactRequest {
  orderId: UUID;
  contact: ICreateInternalContactData;
}

interface UpdateInternalContactRequest {
  orderId: UUID;
  contact: IInternalContactData;
}

interface DeleteInternalContactRequest {
  orderId: UUID;
  contactId: UUID;
}

interface AddThirdPartyContactRequest {
  orderId: UUID;
  contact: IThirdPartyContactData;
}

interface UpdateThirdPartyContactRequest {
  orderId: UUID;
  contact: IThirdPartyContactData;
}

interface DeleteThirdPartyContactRequest {
  orderId: UUID;
  contactId: UUID;
}

interface AddClientContactRequest {
  orderId: UUID;
  contact: IClientContactData;
}

interface UpdateClientContactRequest {
  orderId: UUID;
  contact: IClientContactData;
}

interface DeleteClientContactRequest {
  orderId: UUID;
  contactId: UUID;
}

interface AddLenderContactRequest {
  orderId: UUID;
  contact: ILenderContactData;
}

interface UpdateLenderContactRequest {
  orderId: UUID;
  contact: ILenderContactData;
}

interface DeleteLenderContactRequest {
  orderId: UUID;
  contactId: UUID;
}

export const OrderContactsAPI = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    // PERSONS
    listPersonOrderContacts: build.query<IConversationContactData[], UUID>({
      query: (orderId) => `/timber/api/internal/orders/${orderId}/persons`,
      providesTags: (result, error, orderId) => [{ type: eAPITags.OrderInternalContacts, id: orderId }],
    }),

    // INTERNAL CONTACTS
    listInternalOrderContacts: build.query<IInternalContactData[], UUID>({
      query: (orderId) => `/timber/api/internal/orders/${orderId}/internal_contacts`,
      providesTags: (result, error, orderId) => [
        { type: eAPITags.OrderInternalContacts, id: orderId },
        { type: eAPITags.OrderInternalContacts, id: 'LIST' },
      ],
    }),

    addInternalOrderContacts: build.mutation<IInternalContactData[], AddInternalContactRequest>({
      query: ({ orderId, contact }) => ({
        url: `/timber/api/internal/orders/${orderId}/internal_contacts`,
        method: 'POST',
        body: convertFromICreateInternalContactDataToICreateInternalContactRequest(contact),
      }),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderInternalContacts, id: orderId }],
    }),

    updateInternalOrderContacts: build.mutation<IInternalContactData, UpdateInternalContactRequest>({
      query: ({ orderId, contact }) => ({
        url: `/timber/api/internal/orders/${orderId}/internal_contacts/${contact.id}`,
        method: 'PUT',
        body: convertFromIInternalContactsDataToIInternalContactsRequest(contact),
      }),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderInternalContacts, id: orderId }],
    }),

    deleteInternalOrderContacts: build.mutation<IInternalContactData, DeleteInternalContactRequest>({
      query: ({ orderId, contactId }) => ({
        url: `/timber/api/internal/orders/${orderId}/internal_contacts/${contactId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderInternalContacts, id: orderId }],
    }),

    // THIRD PARTY CONTACTS
    listThirdPartyContacts: build.query<IThirdPartyContactData[], UUID>({
      query: (orderId) => `/timber/api/internal/orders/${orderId}/third_party_contacts`,
      transformResponse: (response: IThirdPartyContactRequest[]) =>
        convertFromIThirdPartyContactRequestArrayToIThirdPartyContactDataArray(response),
      providesTags: (result, error, orderId) => [{ type: eAPITags.OrderThirdPartyContacts, id: orderId }],
    }),

    addThirdPartyContacts: build.mutation<IThirdPartyContactData, AddThirdPartyContactRequest>({
      query: ({ orderId, contact }) => ({
        url: `/timber/api/internal/orders/${orderId}/third_party_contacts`,
        method: 'POST',
        body: convertFromIThirdPartyContactDataToIThirdPartyContactRequest(contact),
      }),
      transformResponse: (response: IThirdPartyContactRequest) =>
        convertFromIThirdPartyContactRequestToIThirdPartyContactData(response),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderThirdPartyContacts, id: orderId }],
    }),

    updateThirdPartyContacts: build.mutation<IThirdPartyContactData, UpdateThirdPartyContactRequest>({
      query: ({ orderId, contact }) => ({
        url: `/timber/api/internal/orders/${orderId}/third_party_contacts/${contact.id}`,
        method: 'PUT',
        body: convertFromIThirdPartyContactDataToIThirdPartyContactRequest(contact),
      }),
      transformResponse: (response: IThirdPartyContactRequest) =>
        convertFromIThirdPartyContactRequestToIThirdPartyContactData(response),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderThirdPartyContacts, id: orderId }],
    }),

    deleteThirdPartyContacts: build.mutation<void, DeleteThirdPartyContactRequest>({
      query: ({ orderId, contactId }) => ({
        url: `/timber/api/internal/orders/${orderId}/third_party_contacts/${contactId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderThirdPartyContacts, id: orderId }],
    }),

    // CLIENT CONTACTS
    listClientOrderContacts: build.query<IClientContactData[], UUID>({
      query: (orderId) => `/timber/api/internal/orders/${orderId}/client_contacts`,
      transformResponse: (response: IClientContactResponse[]) =>
        convertFromClientContactResponseArrayToClientContactDataArray(response),
      providesTags: (result, error, orderId) => [{ type: eAPITags.OrderClientContacts, id: orderId }],
    }),

    addClientOrderContact: build.mutation<IClientContactData, AddClientContactRequest>({
      query: ({ orderId, contact }) => ({
        url: `/timber/api/internal/orders/${orderId}/client_contacts`,
        method: 'POST',
        body: convertFromIClientContactDataToICreateClientOrderContactRequest(contact),
      }),
      transformResponse: (response: IClientContactResponse) =>
        convertFromClientContactResponseToClientContactData(response),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderClientContacts, id: orderId }],
    }),

    updateClientOrderContact: build.mutation<IClientContactData, UpdateClientContactRequest>({
      query: ({ orderId, contact }) => ({
        url: `/timber/api/internal/orders/${orderId}/client_contacts/${contact.id}`,
        method: 'PUT',
        body: convertFromClientContactDataToClientContactResponse(contact),
      }),
      transformResponse: (response: IClientContactResponse) =>
        convertFromClientContactResponseToClientContactData(response),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderClientContacts, id: orderId }],
    }),

    deleteClientOrderContact: build.mutation<void, DeleteClientContactRequest>({
      query: ({ orderId, contactId }) => ({
        url: `/timber/api/internal/orders/${orderId}/client_contacts/${contactId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderClientContacts, id: orderId }],
    }),

    // LENDER CONTACTS
    listLenderOrderContacts: build.query<ILenderContactData[], UUID>({
      query: (orderId) => `/timber/api/internal/orders/${orderId}/lender_contacts`,
      transformResponse: (response: ILenderContactResponse[]) =>
        convertFromLenderOrderContactResponseArrayToLenderContactDataArray(response),
      providesTags: (result, error, orderId) => [{ type: eAPITags.OrderLenderContacts, id: orderId }],
    }),

    addLenderOrderContact: build.mutation<ILenderContactData, AddLenderContactRequest>({
      query: ({ orderId, contact }) => ({
        url: `/timber/api/internal/orders/${orderId}/lender_contacts`,
        method: 'POST',
        body: convertFromILenderContactDataToICreateLenderOrderContactRequest(contact),
      }),
      transformResponse: (response: ILenderContactResponse) =>
        convertFromLenderContactResponseToLenderContactData(response),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderLenderContacts, id: orderId }],
    }),

    updateLenderOrderContact: build.mutation<ILenderContactData, UpdateLenderContactRequest>({
      query: ({ orderId, contact }) => ({
        url: `/timber/api/internal/orders/${orderId}/lender_contacts/${contact.id}`,
        method: 'PUT',
        body: convertFromLenderContactDataToLenderContactResponse(contact),
      }),
      transformResponse: (response: ILenderContactResponse) =>
        convertFromLenderContactResponseToLenderContactData(response),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderLenderContacts, id: orderId }],
    }),

    deleteLenderOrderContact: build.mutation<void, DeleteLenderContactRequest>({
      query: ({ orderId, contactId }) => ({
        url: `/timber/api/internal/orders/${orderId}/lender_contacts/${contactId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.OrderLenderContacts, id: orderId }],
    }),

    // SIGNATORY CONTACTS
    listSignatoryOrderContacts: build.query<IConversationContactData[], UUID>({
      query: (orderId) => `/timber/api/internal/orders/${orderId}/signatories`,
      providesTags: (result, error, orderId) => [{ type: eAPITags.OrderInternalContacts, id: orderId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  // Persons
  useListPersonOrderContactsQuery,

  // Internal Contacts
  useListInternalOrderContactsQuery,
  useAddInternalOrderContactsMutation,
  useUpdateInternalOrderContactsMutation,
  useDeleteInternalOrderContactsMutation,

  // Third Party Contacts
  useListThirdPartyContactsQuery,
  useAddThirdPartyContactsMutation,
  useUpdateThirdPartyContactsMutation,
  useDeleteThirdPartyContactsMutation,

  // Client Contacts
  useListClientOrderContactsQuery,
  useAddClientOrderContactMutation,
  useUpdateClientOrderContactMutation,
  useDeleteClientOrderContactMutation,

  // Lender Contacts
  useListLenderOrderContactsQuery,
  useAddLenderOrderContactMutation,
  useUpdateLenderOrderContactMutation,
  useDeleteLenderOrderContactMutation,

  useListSignatoryOrderContactsQuery,
} = OrderContactsAPI;
